import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import { Badge } from '@/shared/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2, Paperclip, X } from 'lucide-react';
import CustomCombobox from '@/shared/ui/custom/combobox';
import {
  AddNewItemTypeDialog,
  AddNewTradeDialog,
  AddNewCostCodeDialog,
} from '@/shared/forms';
import {
  usePunchListItemTypes,
  useTrades,
  useTradePartners,
  useCostCodes,
  useCreateCostCode,
  usePunchListMutations,
  useProjectUsers,
  useProjectAreaTags,
  useProjectLayouts,
  useCreateAreaTag,
  useCreatePunchListItemType,
  useCreateTrade,
} from '../hooks';
import type {
  CreatePunchListItemData,
  ImpactChoice,
  PunchListItem,
  PunchListPriority,
  UserMinimal,
} from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | undefined;
  item?: PunchListItem | null;
}

const IMPACT_OPTIONS: { value: ImpactChoice; label: string }[] = [
  { value: 'YES', label: 'Yes' },
  { value: 'YES_UNKNOWN', label: 'Yes (Unknown)' },
  { value: 'NO', label: 'No' },
  { value: 'TBD', label: 'TBD' },
  { value: 'NA', label: 'N/A' },
];

const PRIORITY_OPTIONS: { value: PunchListPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

const INITIAL_FORM: Omit<CreatePunchListItemData, 'project'> = {
  title: '',
  priority: 'MEDIUM',
  status: 'OPEN',
  description: '',
  cost_impact: 'NA',
  schedule_impact: 'NA',
  is_private: false,
  due_date: '',
  punch_manager: null,
  assignee: null,
  final_approver: null,
  item_type: null,
  trade: null,
  responsible_trade_partner: null,
  cost_code: null,
  location: null,
  distribution_list: [],
};

const userDisplayName = (u: UserMinimal) =>
  `${u.first_name} ${u.last_name}`.trim() || u.email;

// ─── Add New Location dialog ──────────────────────────────────────────────────

interface AddNewLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchValue: string;
  onConfirm: (value: { name: string; layoutId: number }) => void;
  layouts: { id: number; name: string; type: string }[];
}

const AddNewLocationDialog: React.FC<AddNewLocationDialogProps> = ({
  open,
  onOpenChange,
  searchValue,
  onConfirm,
  layouts,
}) => {
  const [name, setName] = useState(searchValue);
  const [layoutId, setLayoutId] = useState<string>('');

  React.useEffect(() => {
    if (open) {
      setName(searchValue);
      setLayoutId('');
    }
  }, [open, searchValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (name.trim() && layoutId) {
      onConfirm({ name: name.trim(), layoutId: Number(layoutId) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New Location</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1">
            <Label htmlFor="new-location-name">Name</Label>
            <Input
              id="new-location-name"
              placeholder="e.g. Level 2 – North Wing"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label>Floor Plan</Label>
            <Select value={layoutId} onValueChange={setLayoutId}>
              <SelectTrigger>
                <SelectValue placeholder="Select floor plan" />
              </SelectTrigger>
              <SelectContent>
                {layouts.map((l) => (
                  <SelectItem key={l.id} value={String(l.id)}>
                    {l.name}{l.type ? ` (${l.type})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {layouts.length === 0 && (
              <p className="text-xs text-muted-foreground">No floor plans found for this project.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || !layoutId}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main dialog ──────────────────────────────────────────────────────────────

// Mount the form only while open: a hidden Radix dialog still runs its hooks.
export const PunchListCreateDialog: React.FC<Props> = (props) =>
  props.open ? <PunchListCreateDialogContent {...props} /> : null;

const PunchListCreateDialogContent: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  projectId,
  item,
}) => {
  const { createItem, updateItem, uploadAttachment } = usePunchListMutations(projectId);
  const isEdit = !!item;
  const { data: itemTypes } = usePunchListItemTypes();
  const { data: trades } = useTrades();
  const { data: tradePartners } = useTradePartners(projectId);
  const { data: costCodes } = useCostCodes();
  const { data: projectUsers } = useProjectUsers(projectId);
  const { data: areaTags } = useProjectAreaTags(projectId);
  const { data: layouts = [] } = useProjectLayouts(projectId);
  const createItemType = useCreatePunchListItemType();
  const createAreaTag = useCreateAreaTag(projectId);
  const createTrade = useCreateTrade();
  const createCostCode = useCreateCostCode();

  const [form, setForm] = useState(INITIAL_FORM);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) return;
    if (!item) {
      setForm(INITIAL_FORM);
      return;
    }
    setForm({
      title: item.title,
      priority: item.priority,
      status: item.status,
      description: item.description ?? '',
      cost_impact: item.cost_impact,
      schedule_impact: item.schedule_impact,
      is_private: item.is_private,
      due_date: item.due_date?.slice(0, 16) ?? '',
      punch_manager: item.punch_manager?.id ?? null,
      assignee: item.assignee?.id ?? null,
      final_approver: item.final_approver?.id ?? null,
      item_type: item.item_type?.id ?? null,
      trade: item.trade?.id ?? null,
      responsible_trade_partner: item.responsible_trade_partner?.id ?? null,
      cost_code: item.cost_code?.id ?? null,
      location: item.location?.id ?? null,
      distribution_list: item.distribution_list.map((user) => user.id),
    });
  }, [item, open]);

  const set = <K extends keyof typeof INITIAL_FORM>(key: K, value: (typeof INITIAL_FORM)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setNullableUser = (key: 'punch_manager' | 'assignee' | 'final_approver', raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw === 'none' ? null : Number(raw) }));

  const setNullableLocation = (raw: string) =>
    setForm((prev) => ({ ...prev, location: raw === 'none' || raw === '' ? null : Number(raw) }));

  const toggleDistribution = (userId: number) => {
    setForm((prev) => {
      const current = prev.distribution_list as number[];
      return {
        ...prev,
        distribution_list: current.includes(userId)
          ? current.filter((id) => id !== userId)
          : [...current, userId],
      };
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFile = (index: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));

  const handleAddNewType = async (name: string) => {
    const newType = await createItemType.mutateAsync(name);
    set('item_type', newType.id);
  };

  const handleAddNewTrade = async (name: string) => {
    const newTrade = await createTrade.mutateAsync(name);
    set('trade', newTrade.id);
  };

  const handleAddNewCostCode = async (value: string) => {
    const parsed = JSON.parse(value) as { code: string; name: string };
    const newCostCode = await createCostCode.mutateAsync(parsed);
    set('cost_code', newCostCode.id);
  };

  const handleAddNewLocation = async (value: { name: string; layoutId: number }) => {
    const newTag = await createAreaTag.mutateAsync({ name: value.name, layout_id: value.layoutId });
    setNullableLocation(String(newTag.id));
  };

  // Stable AddNewDialog wrapper for the location combobox — passes the already-
  // fetched `layouts` array down as a plain prop so AddNewLocationDialog never
  // has to run its own query (which was the source of the populate failure).
  const LocationAddNewDialog = React.useCallback(
    (props: Omit<AddNewLocationDialogProps, 'layouts'>) => (
      <AddNewLocationDialog {...props} layouts={layouts} />
    ),
    [layouts],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    setError(null);
    try {
      const payload: CreatePunchListItemData = {
        ...form,
        project: projectId ?? 0,
        due_date: form.due_date || null,
        punch_manager: form.punch_manager || null,
        assignee: form.assignee || null,
        final_approver: form.final_approver || null,
        item_type: form.item_type || null,
        trade: form.trade || null,
        responsible_trade_partner: form.responsible_trade_partner || null,
        cost_code: form.cost_code || null,
        location: form.location || null,
      };
      const savedItem = isEdit && item
        ? await updateItem.mutateAsync({ id: item.id, data: payload })
        : await createItem.mutateAsync(payload);
      for (const file of pendingFiles) {
        await uploadAttachment.mutateAsync({ itemId: savedItem.id, file });
      }
      setForm(INITIAL_FORM);
      setPendingFiles([]);
      onSuccess?.();
      onClose();
    } catch {
      setError(`Failed to ${isEdit ? 'update' : 'create'} punch list item. Please try again.`);
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setPendingFiles([]);
    setError(null);
    onClose();
  };

  const distList = (form.distribution_list as number[]) ?? [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Punch List Item' : 'Create Punch List Item'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? 'Update this punch list item.' : 'Fill in the details below to create a new punch list item.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">

          {/* Title */}
          <div className="space-y-1">
            <Label htmlFor="title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Describe the punch item"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
            />
          </div>

          {/* Row: Priority + Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(v) => set('priority', v as PunchListPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Type</Label>
              <CustomCombobox
                options={(itemTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
                value={form.item_type ?? ''}
                onChange={(v) => set('item_type', v || null)}
                placeholder="Select type"
                searchPlaceholder="Search types…"
                addNewLabel="Add New Type"
                emptyMessage="No types found."
                onAddNew={handleAddNewType}
                AddNewDialog={AddNewItemTypeDialog}
              />
            </div>
          </div>

          {/* Punch Manager */}
          <div className="space-y-1">
            <Label>Punch Manager</Label>
            <Select
              value={form.punch_manager != null ? String(form.punch_manager) : 'none'}
              onValueChange={(v) => setNullableUser('punch_manager', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select punch manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {(projectUsers ?? []).map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {userDisplayName(u)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row: Assignee + Final Approver */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Assignee</Label>
              <Select
                value={form.assignee != null ? String(form.assignee) : 'none'}
                onValueChange={(v) => setNullableUser('assignee', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(projectUsers ?? []).map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {userDisplayName(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Final Approver</Label>
              <Select
                value={form.final_approver != null ? String(form.final_approver) : 'none'}
                onValueChange={(v) => setNullableUser('final_approver', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select final approver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(projectUsers ?? []).map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {userDisplayName(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-1">
            <Label>Location</Label>
            <CustomCombobox
              options={(areaTags ?? []).map((tag) => ({ value: String(tag.id), label: tag.name }))}
              value={form.location != null ? String(form.location) : ''}
              onChange={(v) => setNullableLocation(v || 'none')}
              placeholder="Select location"
              searchPlaceholder="Search locations…"
              addNewLabel="Add New Location"
              emptyMessage="No locations found."
              onAddNew={handleAddNewLocation}
              AddNewDialog={LocationAddNewDialog}
            />
          </div>

          {/* Due Date */}
          <div className="space-y-1">
            <Label htmlFor="due_date">Due Date</Label>
            <Input
              id="due_date"
              type="datetime-local"
              value={form.due_date ?? ''}
              onChange={(e) => {
                const val = e.target.value; // "YYYY-MM-DDTHH:MM"
                if (!val) {
                  set('due_date', val);
                  return;
                }
                const prevDate = (form.due_date ?? '').slice(0, 10);
                const newDate = val.slice(0, 10);
                const newTime = val.slice(11); // "HH:MM"
                const prevTime = (form.due_date ?? '').slice(11);
                // If the date changed but the time stayed the same as before
                // (browser just carried it over), snap to 23:59.
                if (newDate !== prevDate && newTime === prevTime) {
                  set('due_date', `${newDate}T23:59`);
                } else if (!form.due_date) {
                  // First selection ever — always default to 23:59
                  set('due_date', `${newDate}T23:59`);
                } else {
                  set('due_date', val);
                }
              }}
            />
          </div>

          {/* Row: Cost Impact + Schedule Impact */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Cost Impact</Label>
              <Select
                value={form.cost_impact}
                onValueChange={(v) => set('cost_impact', v as ImpactChoice)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMPACT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Schedule Impact</Label>
              <Select
                value={form.schedule_impact}
                onValueChange={(v) => set('schedule_impact', v as ImpactChoice)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMPACT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row: Trade + Responsible Trade Partner */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Trade</Label>
              <CustomCombobox
                options={(trades ?? []).map((t) => ({ value: t.id, label: t.name }))}
                value={form.trade ?? ''}
                onChange={(v) => set('trade', v || null)}
                placeholder="Select trade"
                searchPlaceholder="Search trades…"
                addNewLabel="Add New Trade"
                emptyMessage="No trades found."
                onAddNew={handleAddNewTrade}
                AddNewDialog={AddNewTradeDialog}
              />
            </div>

            <div className="space-y-1">
              <Label>Responsible Trade Partner</Label>
              <CustomCombobox
                options={(tradePartners ?? []).map((partner) => ({
                  value: String(partner.id),
                  label: partner.trade?.name
                    ? `${partner.company_name} · ${partner.trade.name}`
                    : partner.company_name,
                }))}
                value={form.responsible_trade_partner != null ? String(form.responsible_trade_partner) : ''}
                onChange={(v) => set('responsible_trade_partner', v ? Number(v) : null)}
                placeholder="Select trade partner"
                searchPlaceholder="Search partners…"
                emptyMessage="No trade partners found."
                showAddNew={false}
              />
            </div>
          </div>

          {/* Cost Code */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Cost Code</Label>
              <CustomCombobox
                options={(costCodes ?? []).map((c) => ({ value: c.id, label: `${c.code} – ${c.name}` }))}
                value={form.cost_code ?? ''}
                onChange={(v) => set('cost_code', v || null)}
                placeholder="Select cost code"
                searchPlaceholder="Search cost codes…"
                addNewLabel="Add New Cost Code"
                emptyMessage="No cost codes found."
                onAddNew={handleAddNewCostCode}
                AddNewDialog={AddNewCostCodeDialog}
              />
            </div>
          </div>

          {/* Distribution List */}
          <div className="space-y-2">
            <Label>Distribution List</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-1">
              {(projectUsers ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No users available</p>
              ) : (
                (projectUsers ?? []).map((u) => (
                  <div key={u.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`dist-${u.id}`}
                      checked={distList.includes(u.id)}
                      onCheckedChange={() => toggleDistribution(u.id)}
                    />
                    <label htmlFor={`dist-${u.id}`} className="text-sm cursor-pointer">
                      {userDisplayName(u)}
                    </label>
                  </div>
                ))
              )}
            </div>
            {distList.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {distList.map((id) => {
                  const u = (projectUsers ?? []).find((u) => u.id === id);
                  return u ? (
                    <Badge key={id} variant="secondary" className="gap-1">
                      {userDisplayName(u)}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => toggleDistribution(id)}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the issue and possible resolution"
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label>Attachments</Label>
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-4 w-4" />
                Attach Files
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
            {pendingFiles.length > 0 && (
              <ul className="space-y-1">
                {pendingFiles.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-sm border rounded px-2 py-1">
                    <span className="truncate max-w-xs">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="ml-2 text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Private */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="is_private"
              checked={form.is_private}
              onCheckedChange={(checked) => set('is_private', checked === true)}
            />
            <Label htmlFor="is_private" className="cursor-pointer">
              Private — only visible to creator, assignee, and distribution list
            </Label>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createItem.isPending || updateItem.isPending}>
              {(createItem.isPending || updateItem.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
